/**
 * <locations-grid> — client-side country filtering + pagination for the
 * Locations section. All cards are rendered server-side; this element shows
 * only the cards matching the active tab and current page.
 */
class LocationsGrid extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('.locations__grid');
    this.cards = Array.from(this.querySelectorAll('.locations__card'));
    this.pagination = this.querySelector('.locations__pagination');
    this.pagesContainer = this.querySelector('[data-pages]');
    this.prevButton = this.querySelector('[data-page-prev]');
    this.nextButton = this.querySelector('[data-page-next]');

    // Tabs live in the section header, outside this element.
    this.section = this.closest('.shopify-section') || document;
    this.tabs = Array.from(this.section.querySelectorAll('.locations__tab'));

    this.perPage = this.hasAttribute('data-no-pagination')
      ? Infinity
      : parseInt(this.getAttribute('data-per-page'), 10) || 8;

    this.filter = 'all';
    this.page = 1;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.setFilter(tab.dataset.filter));
    });

    if (this.pagination) {
      this.prevButton?.addEventListener('click', () => this.setPage(this.page - 1));
      this.nextButton?.addEventListener('click', () => this.setPage(this.page + 1));
    }

    this.render();
  }

  get filteredCards() {
    if (this.filter === 'all') return this.cards;
    return this.cards.filter((card) => card.dataset.country === this.filter);
  }

  get totalPages() {
    return Math.max(1, Math.ceil(this.filteredCards.length / this.perPage));
  }

  setFilter(filter) {
    if (filter === this.filter) return;
    this.filter = filter;
    this.page = 1;
    this.tabs.forEach((tab) => {
      tab.setAttribute('aria-selected', tab.dataset.filter === filter ? 'true' : 'false');
    });
    this.render();
  }

  setPage(page) {
    const target = Math.min(Math.max(1, page), this.totalPages);
    if (target === this.page) return;
    this.page = target;
    this.render();
    this.scrollIntoViewTop();
  }

  scrollIntoViewTop() {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  }

  render() {
    const filtered = this.filteredCards;
    const start = (this.page - 1) * this.perPage;
    const end = start + this.perPage;
    const visible = new Set(filtered.slice(start, end));

    this.cards.forEach((card) => {
      card.hidden = !visible.has(card);
    });

    this.renderPagination();
  }

  renderPagination() {
    if (!this.pagination) return;

    const total = this.totalPages;
    if (total <= 1) {
      this.pagination.hidden = true;
      return;
    }
    this.pagination.hidden = false;

    this.prevButton.disabled = this.page === 1;
    this.nextButton.disabled = this.page === total;

    this.pagesContainer.innerHTML = '';
    LocationsGrid.pageItems(total, this.page).forEach((item) => {
      if (item === '…') {
        const span = document.createElement('span');
        span.className = 'locations__ellipsis';
        span.textContent = '…';
        this.pagesContainer.appendChild(span);
        return;
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'locations__page';
      button.textContent = item;
      if (item === this.page) {
        button.setAttribute('aria-current', 'page');
      }
      button.addEventListener('click', () => this.setPage(item));
      this.pagesContainer.appendChild(button);
    });
  }

  /**
   * Build a windowed page list: first three pages, the current page's
   * neighbours, and the last page, with '…' inserted across gaps.
   */
  static pageItems(total, current) {
    const wanted = new Set([1, 2, 3, total, current - 1, current, current + 1]);
    const pages = [...wanted].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);

    const items = [];
    let previous = 0;
    pages.forEach((page) => {
      if (page - previous > 1) items.push('…');
      items.push(page);
      previous = page;
    });
    return items;
  }
}

customElements.define('locations-grid', LocationsGrid);
